import React from "react";
import type { SiteConfig } from "@/types/sites";
import type { LayoutConfig } from "@/types/layout";
import { useLandingPageTheme } from "@/components/providers/LandingPageThemeProvider";
import { getPaletteClass } from "@/utils/paletteUtils";
import { getDarkModeBackground } from "./backgroundUtils";
import { cn } from "@/lib/utils";

interface SectionBackgroundRendererProps {
  config: SiteConfig;
  sectionIndex: number;
  children: React.ReactNode;
  sectionName: string;
}

const SectionBackgroundRenderer = ({
  config,
  sectionIndex,
  children,
  sectionName,
}: SectionBackgroundRendererProps) => {
  const { isDarkMode } = useLandingPageTheme();
  const alternateConfig = config?.alternateBackgrounds;

  // Check if this section has darkMode enabled in its layout config
  const sectionLayout = config?.layout?.[sectionName as keyof LayoutConfig];
  const sectionDarkMode =
    sectionLayout &&
    typeof sectionLayout === "object" &&
    "darkMode" in sectionLayout
      ? (sectionLayout as { darkMode?: boolean }).darkMode
      : false;

  // If alternate backgrounds are not enabled or not configured, render children with optional dark mode wrapper
  if (!alternateConfig?.enabled || sectionIndex === -1) {
    return (
      <div id={sectionName} className={cn(sectionDarkMode && "dark")}>
        {children}
      </div>
    );
  }

  // Dark mode: Use the configurable dark mode background modes
  if (isDarkMode) {
    const darkBg = getDarkModeBackground(config, sectionName, sectionIndex);
    return (
      <div className={cn("relative", sectionDarkMode && "dark")}>
        <div
          className={`absolute inset-0 ${darkBg.className}`}
          style={{ zIndex: 0, pointerEvents: "none", ...darkBg.style }}
          data-section-background={sectionName}
          data-section-index={sectionIndex}
          data-dark-mode={isDarkMode}
          data-dark-mode-bg-mode={
            config.alternateBackgrounds?.darkModeBackgroundMode ?? "advanced"
          }
          aria-hidden="true"
        />
        <div className="relative z-10">{children}</div>
      </div>
    );
  }

  // Light mode: Use palette-based backgrounds
  // Even sections (0, 2, 4...) get PRIMARY (white), Odd sections (1, 3, 5...) get ALTERNATE (colored)
  const isEvenSection = sectionIndex % 2 === 0;
  const backgroundClass = isEvenSection
    ? getPaletteClass(config?.theme, "backgroundPrimary", "bg-white") // Even = Primary (white)
    : getPaletteClass(config?.theme, "backgroundSoft", "bg-gray-50"); // Odd = Soft (colored)

  return (
    <div className={cn("relative", sectionDarkMode && "dark")}>
      <div
        className={`absolute inset-0 ${backgroundClass}`}
        style={{ zIndex: 0, pointerEvents: "none" }}
        data-section-background={sectionName}
        data-alternate-section={!isEvenSection}
        data-section-index={sectionIndex}
        data-dark-mode={isDarkMode}
        aria-hidden="true"
      />
      <div className="relative z-10">{children}</div>
    </div>
  );
};

export default SectionBackgroundRenderer;
